import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Center,
  ColorInput,
  Drawer,
  Group,
  Image,
  Loader,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconAnchor,
  IconBed,
  IconBriefcase,
  IconBuilding,
  IconCalendarEvent,
  IconCar,
  IconCheck,
  IconCircleCheck,
  IconExternalLink,
  IconGauge,
  IconGasStation,
  IconKey,
  IconLock,
  IconPlane,
  IconPlus,
  IconQuestionMark,
  IconRefresh,
  IconSearch,
  IconSparkles,
  IconTrain,
  IconX,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';
import { useAuth } from '@/app/providers/AuthProvider';
import { createOne, fetchList } from '@/shared/api/jsonapi/client';
import { PageHeader } from '@/shared/components/PageHeader';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { getRequestType } from '@/features/bookings/bookingFullApi';
import type { VehicleFull } from '@/features/bookings/bookingFullApi';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { EmptyState } from '@/shared/components/EmptyState';

interface VehicleRow extends Denormalized, VehicleFull {
  type: 'vehicles';
  id: string;
}

type ViewMode = 'cards' | 'table';
type StatusFilter = 'all' | 'active' | 'blocked' | 'expiring';

const TYPE_OPTIONS = [
  { value: '', label: 'Tutti i tipi' },
  { value: '1', label: 'Auto' },
  { value: '2', label: 'Sala riunione' },
  { value: '3', label: 'Treno' },
  { value: '4', label: 'Aereo' },
  { value: '5', label: 'Hotel' },
  { value: '6', label: 'Noleggio' },
  { value: '7', label: 'Nave' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Tutti gli stati' },
  { value: 'active', label: 'Attivi' },
  { value: 'blocked', label: 'Bloccati' },
  { value: 'expiring', label: 'Scadenze entro 30gg' },
];

const FUEL_LABELS: Record<string, string> = {
  petrol: 'Benzina',
  diesel: 'Diesel',
  electric: 'Elettrico',
  hybrid: 'Ibrido',
};

function typeIcon(t: number | undefined) {
  switch (t) {
    case 1: return IconCar;
    case 2: return IconBuilding;
    case 3: return IconTrain;
    case 4: return IconPlane;
    case 5: return IconBed;
    case 6: return IconKey;
    case 7: return IconAnchor;
    default: return IconQuestionMark;
  }
}

interface ExpiryStatus {
  level: 'expired' | 'soon' | 'ok' | 'none';
  daysLeft: number | null;
}

function getExpiryStatus(iso: string | undefined): ExpiryStatus {
  if (!iso) return { level: 'none', daysLeft: null };
  const days = dayjs(iso).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (days < 0) return { level: 'expired', daysLeft: days };
  if (days <= 30) return { level: 'soon', daysLeft: days };
  return { level: 'ok', daysLeft: days };
}

function expiryColor(level: ExpiryStatus['level']) {
  if (level === 'expired') return 'red';
  if (level === 'soon') return 'orange';
  if (level === 'ok') return 'green';
  return 'gray';
}

function nearestCritical(v: VehicleFull): ExpiryStatus & { label?: string } {
  const items: { iso: string | undefined; label: string }[] = [
    { iso: v.insuranceExpiry, label: 'Assicurazione' },
    { iso: v.revisionExpiry, label: 'Revisione' },
    { iso: v.taxExpiry, label: 'Bollo' },
  ];
  let best: ExpiryStatus & { label?: string } = { level: 'none', daysLeft: null };
  for (const it of items) {
    const s = getExpiryStatus(it.iso);
    if (s.level === 'none') continue;
    if (best.level === 'none') {
      best = { ...s, label: it.label };
    } else if (
      (s.level === 'expired' && best.level !== 'expired') ||
      (s.level === 'soon' && best.level === 'ok') ||
      (s.daysLeft !== null && best.daysLeft !== null && s.daysLeft < best.daysLeft)
    ) {
      best = { ...s, label: it.label };
    }
  }
  return best;
}

export const Route = createFileRoute('/_app/vehicles')({ component: VehiclesPage });

function VehiclesPage() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const isSuperAdmin = decoded?.superadmin === 'True';
  const [view, setView] = useState<ViewMode>('cards');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selected, setSelected] = useState<VehicleRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ['vehicles-fleet'],
    queryFn: async () => {
      const { data } = await fetchList<VehicleRow>('vehicles', { page: { size: 500 } });
      return data;
    },
  });

  const all = useMemo(() => listQ.data ?? [], [listQ.data]);

  const stats = useMemo(() => {
    const total = all.length;
    const blocked = all.filter((v) => v.isBlocked).length;
    const active = total - blocked;
    const expiring = all.filter((v) => {
      const n = nearestCritical(v);
      return n.level === 'soon' || n.level === 'expired';
    }).length;
    return { total, active, blocked, expiring };
  }, [all]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((v) => {
      if (q) {
        const hay = `${v.name ?? ''} ${v.code ?? ''} ${v.fuelType ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter && String(v.vehicleType ?? v.icon ?? '') !== typeFilter) return false;
      if (statusFilter === 'active' && v.isBlocked) return false;
      if (statusFilter === 'blocked' && !v.isBlocked) return false;
      if (statusFilter === 'expiring') {
        const n = nearestCritical(v);
        if (n.level !== 'soon' && n.level !== 'expired') return false;
      }
      return true;
    });
  }, [all, search, typeFilter, statusFilter]);

  return (
    <Stack gap="lg">
      <PageHeader
        title="Parco mezzi"
        description="Flotta aziendale: stato, scadenze e disponibilità in un colpo d'occhio."
        actions={
          <>
            <Tooltip label="Aggiorna">
              <ActionIcon
                variant="light"
                size="lg"
                aria-label="Aggiorna parco mezzi"
                onClick={() => qc.invalidateQueries({ queryKey: ['vehicles-fleet'] })}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            {(isSuperAdmin || all.length === 0) && (
              <SeedDemoButton onSeeded={() => qc.invalidateQueries({ queryKey: ['vehicles-fleet'] })} />
            )}
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              Nuovo mezzo
            </Button>
            <Button
              component={Link}
              to="/secretary/area"
              variant="subtle"
              leftSection={<IconBriefcase size={16} />}
              rightSection={<IconExternalLink size={14} />}
            >
              Gestione completa
            </Button>
          </>
        }
      />

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <StatBox label="Totale mezzi" value={stats.total} icon={IconCar} color="blue" />
        <StatBox label="Attivi" value={stats.active} icon={IconCircleCheck} color="green" />
        <StatBox label="Bloccati" value={stats.blocked} icon={IconLock} color="red" />
        <StatBox label="Scadenze ≤30gg" value={stats.expiring} icon={IconAlertTriangle} color="orange" />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
            <TextInput
              placeholder="Cerca per nome, codice o carburante…"
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={280}
              rightSection={
                search ? (
                  <ActionIcon size="sm" variant="subtle" aria-label="Cancella ricerca" onClick={() => setSearch('')}>
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
            />
            <Select
              data={TYPE_OPTIONS}
              value={typeFilter}
              onChange={(v) => setTypeFilter(v ?? '')}
              w={170}
              leftSection={<IconAdjustments size={14} />}
              placeholder="Tipo"
              clearable={false}
            />
            <Select
              data={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(v) => setStatusFilter((v as StatusFilter) ?? 'all')}
              w={210}
              clearable={false}
            />
            {(search || typeFilter || statusFilter !== 'all') && (
              <Button
                variant="subtle"
                size="xs"
                onClick={() => { setSearch(''); setTypeFilter(''); setStatusFilter('all'); }}
              >
                Pulisci filtri
              </Button>
            )}
          </Group>
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as ViewMode)}
            data={[
              { value: 'cards', label: 'Card' },
              { value: 'table', label: 'Tabella' },
            ]}
          />
        </Group>
        {filtered.length !== all.length && (
          <Text size="xs" c="dimmed" mt="xs">
            {filtered.length} di {all.length} mezzi
          </Text>
        )}
      </Paper>

      {listQ.isError ? (
        <QueryErrorAlert error={listQ.error} onRetry={() => void listQ.refetch()} />
      ) : listQ.isLoading ? (
        <Center py="xl"><Loader /></Center>
      ) : filtered.length === 0 ? (
        <Paper withBorder radius="md">
          <EmptyState
            icon={IconCar}
            title={all.length === 0 ? 'Nessun mezzo in flotta' : 'Nessun mezzo corrisponde ai filtri'}
            description={all.length === 0
              ? 'Apri Gestione completa per aggiungere il primo veicolo.'
              : 'Prova ad allargare i criteri di ricerca.'}
          />
        </Paper>
      ) : view === 'cards' ? (
        <CardsView data={filtered} onOpen={setSelected} />
      ) : (
        <TableView data={filtered} onOpen={setSelected} />
      )}

      <VehicleDetailDrawer vehicle={selected} onClose={() => setSelected(null)} />

      <CreateVehicleModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => qc.invalidateQueries({ queryKey: ['vehicles-fleet'] })}
      />
    </Stack>
  );
}

function StatBox({ label, value, icon: Icon, color }: { label: string; value: number; icon: typeof IconCar; color: string }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
          <Text size="xl" fw={700}>{value}</Text>
        </Stack>
        <ThemeIcon size={38} radius="md" variant="light" color={color}>
          <Icon size={20} />
        </ThemeIcon>
      </Group>
    </Paper>
  );
}

function CardsView({ data, onOpen }: { data: VehicleRow[]; onOpen: (v: VehicleRow) => void }) {
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
      {data.map((v) => (
        <VehicleCard key={v.id} v={v} onOpen={() => onOpen(v)} />
      ))}
    </SimpleGrid>
  );
}

function VehicleCard({ v, onOpen }: { v: VehicleRow; onOpen: () => void }) {
  const type = getRequestType(v.vehicleType ?? v.icon);
  const Icon = typeIcon(v.vehicleType ?? v.icon ?? undefined);
  const expiry = nearestCritical(v);
  const accent = v.color && /^#?[0-9a-f]{3,8}$/i.test(v.color)
    ? (v.color.startsWith('#') ? v.color : `#${v.color}`)
    : undefined;

  return (
    <Card
      withBorder
      radius="md"
      padding={0}
      onClick={onOpen}
      style={{ cursor: 'pointer', overflow: 'hidden', transition: 'transform 120ms, box-shadow 120ms' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      <Box
        style={{
          position: 'relative',
          height: 130,
          background: accent
            ? `linear-gradient(135deg, ${accent}33 0%, var(--mantine-color-gray-1) 100%)`
            : `linear-gradient(135deg, var(--mantine-color-${type.color}-1) 0%, var(--mantine-color-gray-1) 100%)`,
        }}
      >
        {v.photoUrl ? (
          <Image src={v.photoUrl} alt={v.name ?? ''} h={130} fit="cover" />
        ) : (
          <Center h="100%">
            <ThemeIcon size={64} radius="50%" variant="white" color={type.color}>
              <Icon size={36} />
            </ThemeIcon>
          </Center>
        )}
        <Badge
          color={type.color}
          variant="filled"
          style={{ position: 'absolute', top: 8, left: 8 }}
        >
          {type.label}
        </Badge>
        {v.isBlocked ? (
          <Badge
            color="red"
            variant="filled"
            leftSection={<IconLock size={10} />}
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            Bloccato
          </Badge>
        ) : (
          <Badge
            color="green"
            variant="light"
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            Attivo
          </Badge>
        )}
      </Box>
      <Stack gap="xs" p="md">
        <div>
          <Text fw={600} lineClamp={1}>{v.name ?? 'Senza nome'}</Text>
          <Text size="xs" c="dimmed" lineClamp={1}>{v.code ?? '—'}</Text>
        </div>
        <Group gap="xs" wrap="wrap">
          {v.currentKm != null && (
            <Badge size="sm" variant="light" color="gray" leftSection={<IconGauge size={11} />}>
              {v.currentKm.toLocaleString('it-IT')} km
            </Badge>
          )}
          {v.fuelType && (
            <Badge size="sm" variant="light" color="gray" leftSection={<IconGasStation size={11} />}>
              {FUEL_LABELS[v.fuelType] ?? v.fuelType}
            </Badge>
          )}
        </Group>
        {expiry.level !== 'none' && (
          <Group gap={6} wrap="nowrap">
            <ThemeIcon size="sm" radius="xl" variant="light" color={expiryColor(expiry.level)}>
              {expiry.level === 'expired'
                ? <IconAlertTriangle size={12} />
                : expiry.level === 'soon'
                  ? <IconCalendarEvent size={12} />
                  : <IconCheck size={12} />}
            </ThemeIcon>
            <Text size="xs" c={expiryColor(expiry.level)} fw={500}>
              {expiry.level === 'expired'
                ? `${expiry.label} scaduta ${Math.abs(expiry.daysLeft ?? 0)}gg fa`
                : expiry.level === 'soon'
                  ? `${expiry.label} tra ${expiry.daysLeft}gg`
                  : `${expiry.label} ok`}
            </Text>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

function TableView({ data, onOpen }: { data: VehicleRow[]; onOpen: (v: VehicleRow) => void }) {
  return (
    <Paper withBorder radius="md" p={0}>
      <ScrollArea type="auto">
        <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Mezzo</Table.Th>
              <Table.Th>Codice</Table.Th>
              <Table.Th>Tipo</Table.Th>
              <Table.Th>Stato</Table.Th>
              <Table.Th>Km</Table.Th>
              <Table.Th>Carburante</Table.Th>
              <Table.Th>Assicurazione</Table.Th>
              <Table.Th>Revisione</Table.Th>
              <Table.Th>Bollo</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((v) => {
              const type = getRequestType(v.vehicleType ?? v.icon);
              const Icon = typeIcon(v.vehicleType ?? v.icon ?? undefined);
              return (
                <Table.Tr
                  key={v.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onOpen(v)}
                >
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <ThemeIcon size="md" radius="md" variant="light" color={type.color}>
                        <Icon size={14} />
                      </ThemeIcon>
                      <Text fw={500} size="sm">{v.name ?? '—'}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{v.code ?? '—'}</Text></Table.Td>
                  <Table.Td><Badge color={type.color} variant="light">{type.label}</Badge></Table.Td>
                  <Table.Td>
                    {v.isBlocked
                      ? <Badge color="red" leftSection={<IconLock size={10} />}>Bloccato</Badge>
                      : <Badge color="green" variant="light">Attivo</Badge>}
                  </Table.Td>
                  <Table.Td><Text size="sm">{v.currentKm != null ? v.currentKm.toLocaleString('it-IT') : '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm">{v.fuelType ? (FUEL_LABELS[v.fuelType] ?? v.fuelType) : '—'}</Text></Table.Td>
                  <Table.Td><ExpiryCell iso={v.insuranceExpiry} /></Table.Td>
                  <Table.Td><ExpiryCell iso={v.revisionExpiry} /></Table.Td>
                  <Table.Td><ExpiryCell iso={v.taxExpiry} /></Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

function ExpiryCell({ iso }: { iso: string | undefined }) {
  if (!iso) return <Text size="sm" c="dimmed">—</Text>;
  const s = getExpiryStatus(iso);
  return (
    <Tooltip
      label={
        s.level === 'expired'
          ? `Scaduta ${Math.abs(s.daysLeft ?? 0)}gg fa`
          : s.level === 'soon'
            ? `Scade tra ${s.daysLeft}gg`
            : `Mancano ${s.daysLeft}gg`
      }
    >
      <Badge color={expiryColor(s.level)} variant={s.level === 'ok' ? 'light' : 'filled'} size="sm">
        {dayjs(iso).format('DD/MM/YYYY')}
      </Badge>
    </Tooltip>
  );
}

function VehicleDetailDrawer({ vehicle, onClose }: { vehicle: VehicleRow | null; onClose: () => void }) {
  if (!vehicle) return null;
  const v = vehicle;
  const type = getRequestType(v.vehicleType ?? v.icon);
  const Icon = typeIcon(v.vehicleType ?? v.icon ?? undefined);

  return (
    <Drawer
      opened
      onClose={onClose}
      position="right"
      size="md"
      title={
        <Group gap="xs">
          <ThemeIcon size="md" radius="md" variant="light" color={type.color}>
            <Icon size={16} />
          </ThemeIcon>
          <Text fw={600}>{v.name ?? 'Mezzo'}</Text>
        </Group>
      }
    >
      <Stack gap="md">
        {v.photoUrl ? (
          <Image src={v.photoUrl} alt={v.name ?? ''} radius="md" h={180} fit="cover" />
        ) : (
          <Center
            h={180}
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              background: `linear-gradient(135deg, var(--mantine-color-${type.color}-1) 0%, var(--mantine-color-gray-1) 100%)`,
            }}
          >
            <ThemeIcon size={80} radius="50%" variant="white" color={type.color}>
              <Icon size={44} />
            </ThemeIcon>
          </Center>
        )}

        <Group gap="xs">
          <Badge color={type.color}>{type.label}</Badge>
          {v.isBlocked
            ? <Badge color="red" leftSection={<IconLock size={10} />}>Bloccato</Badge>
            : <Badge color="green" variant="light">Attivo</Badge>}
          {v.priority != null && <Badge variant="light" color="gray">Priorità {v.priority}</Badge>}
        </Group>

        <Paper withBorder radius="md" p="md">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <DetailRow label="Codice" value={v.code} />
            <DetailRow label="Km attuali" value={v.currentKm != null ? `${v.currentKm.toLocaleString('it-IT')} km` : undefined} />
            <DetailRow label="Carburante" value={v.fuelType ? (FUEL_LABELS[v.fuelType] ?? v.fuelType) : undefined} />
            <DetailRow label="Azienda" value={v.companyId != null ? `#${v.companyId}` : undefined} />
          </SimpleGrid>
        </Paper>

        <Paper withBorder radius="md" p="md">
          <Text size="sm" fw={600} mb="xs">Scadenze</Text>
          <Stack gap="xs">
            <ExpiryRow label="Assicurazione" iso={v.insuranceExpiry} />
            <ExpiryRow label="Revisione" iso={v.revisionExpiry} />
            <ExpiryRow label="Bollo" iso={v.taxExpiry} />
          </Stack>
        </Paper>

        {v.documentUrl && (
          <Button
            component="a"
            href={v.documentUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="light"
            leftSection={<IconExternalLink size={14} />}
            fullWidth
          >
            Apri libretto / documenti
          </Button>
        )}

        <Button
          component={Link}
          to="/secretary/area"
          variant="subtle"
          leftSection={<IconBriefcase size={14} />}
          fullWidth
        >
          Modifica in Gestione mezzi
        </Button>
      </Stack>
    </Drawer>
  );
}

function DetailRow({ label, value }: { label: string; value: string | undefined | null }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      <Text size="sm">{value ?? '—'}</Text>
    </div>
  );
}

function ExpiryRow({ label, iso }: { label: string; iso: string | undefined }) {
  const s = getExpiryStatus(iso);
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm">{label}</Text>
      {iso ? (
        <Badge color={expiryColor(s.level)} variant={s.level === 'ok' ? 'light' : 'filled'}>
          {dayjs(iso).format('DD/MM/YYYY')}
          {s.daysLeft !== null && (
            <Text component="span" size="xs" ml={6} opacity={0.85}>
              ({s.level === 'expired' ? `−${Math.abs(s.daysLeft)}gg` : `${s.daysLeft}gg`})
            </Text>
          )}
        </Badge>
      ) : (
        <Text size="sm" c="dimmed">—</Text>
      )}
    </Group>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE VEHICLE MODAL
// ─────────────────────────────────────────────────────────────────────────────

interface VehicleDraft {
  name: string;
  code: string;
  vehicleType: string;
  fuelType: string;
  currentKm: number | '';
  color: string;
  priority: number | '';
  isBlocked: boolean;
  photoUrl: string;
  documentUrl: string;
  insuranceExpiry: Date | null;
  revisionExpiry: Date | null;
  taxExpiry: Date | null;
}

const EMPTY_DRAFT: VehicleDraft = {
  name: '',
  code: '',
  vehicleType: '1',
  fuelType: '',
  currentKm: '',
  color: '',
  priority: 1,
  isBlocked: false,
  photoUrl: '',
  documentUrl: '',
  insuranceExpiry: null,
  revisionExpiry: null,
  taxExpiry: null,
};

const CREATE_TYPE_OPTIONS = [
  { value: '1', label: 'Auto' },
  { value: '2', label: 'Sala riunione' },
  { value: '3', label: 'Treno' },
  { value: '4', label: 'Aereo' },
  { value: '5', label: 'Hotel' },
  { value: '6', label: 'Noleggio' },
  { value: '7', label: 'Nave' },
];

const FUEL_OPTIONS = [
  { value: 'petrol', label: 'Benzina' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Elettrico' },
  { value: 'hybrid', label: 'Ibrido' },
];

function CreateVehicleModal({
  opened, onClose, onCreated,
}: { opened: boolean; onClose: () => void; onCreated: () => void }) {
  const [draft, setDraft] = useState<VehicleDraft>(EMPTY_DRAFT);

  const reset = () => setDraft(EMPTY_DRAFT);

  const create = useMutation({
    mutationFn: () => {
      const vt = parseInt(draft.vehicleType, 10);
      return createOne('vehicles', {
        attributes: {
          name: draft.name.trim(),
          code: draft.code.trim() || undefined,
          vehicleType: vt,
          icon: vt,
          fuelType: draft.fuelType || undefined,
          currentKm: typeof draft.currentKm === 'number' ? draft.currentKm : undefined,
          color: draft.color || undefined,
          priority: typeof draft.priority === 'number' ? draft.priority : undefined,
          isBlocked: draft.isBlocked,
          photoUrl: draft.photoUrl.trim() || undefined,
          documentUrl: draft.documentUrl.trim() || undefined,
          insuranceExpiry: draft.insuranceExpiry ? dayjs(draft.insuranceExpiry).format('YYYY-MM-DD') : undefined,
          revisionExpiry: draft.revisionExpiry ? dayjs(draft.revisionExpiry).format('YYYY-MM-DD') : undefined,
          taxExpiry: draft.taxExpiry ? dayjs(draft.taxExpiry).format('YYYY-MM-DD') : undefined,
        },
      });
    },
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Mezzo aggiunto al parco' });
      onCreated();
      reset();
      onClose();
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof Error ? err.message : 'Errore creazione' });
    },
  });

  const canSubmit = draft.name.trim().length > 0;

  return (
    <Modal
      opened={opened}
      onClose={() => { reset(); onClose(); }}
      title={<Group gap={6}><IconCar size={18} /><Text fw={600}>Nuovo mezzo</Text></Group>}
      size="lg"
      centered
    >
      <Stack>
        <Group grow>
          <TextInput
            label="Nome / modello"
            placeholder="Es. Fiat 500 EV"
            required
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.currentTarget.value })}
            data-autofocus
          />
          <TextInput
            label="Codice / targa"
            placeholder="Es. AB123CD"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.currentTarget.value })}
          />
        </Group>

        <Group grow>
          <Select
            label="Tipo"
            data={CREATE_TYPE_OPTIONS}
            value={draft.vehicleType}
            onChange={(v) => setDraft({ ...draft, vehicleType: v ?? '1' })}
            allowDeselect={false}
          />
          <Select
            label="Carburante"
            placeholder="Seleziona…"
            data={FUEL_OPTIONS}
            value={draft.fuelType || null}
            onChange={(v) => setDraft({ ...draft, fuelType: v ?? '' })}
            clearable
          />
        </Group>

        <Group grow>
          <NumberInput
            label="Km attuali"
            min={0}
            value={draft.currentKm}
            onChange={(v) => setDraft({ ...draft, currentKm: typeof v === 'number' ? v : '' })}
            thousandSeparator="."
            decimalSeparator=","
            rightSection={<Text size="xs" c="dimmed" pr={6}>km</Text>}
          />
          <NumberInput
            label="Priorità"
            description="1 = alta, 5 = bassa"
            min={1}
            max={5}
            value={draft.priority}
            onChange={(v) => setDraft({ ...draft, priority: typeof v === 'number' ? v : '' })}
          />
          <ColorInput
            label="Colore accent"
            placeholder="es. #3366FF"
            value={draft.color}
            onChange={(v) => setDraft({ ...draft, color: v })}
            format="hex"
            withEyeDropper={false}
          />
        </Group>

        <Group grow align="flex-start">
          <DateInput
            label="Scadenza assicurazione"
            placeholder="GG/MM/AAAA"
            valueFormat="DD/MM/YYYY"
            value={draft.insuranceExpiry}
            onChange={(v) => setDraft({ ...draft, insuranceExpiry: v })}
            clearable
          />
          <DateInput
            label="Scadenza revisione"
            placeholder="GG/MM/AAAA"
            valueFormat="DD/MM/YYYY"
            value={draft.revisionExpiry}
            onChange={(v) => setDraft({ ...draft, revisionExpiry: v })}
            clearable
          />
          <DateInput
            label="Scadenza bollo"
            placeholder="GG/MM/AAAA"
            valueFormat="DD/MM/YYYY"
            value={draft.taxExpiry}
            onChange={(v) => setDraft({ ...draft, taxExpiry: v })}
            clearable
          />
        </Group>

        <Group grow>
          <TextInput
            label="URL foto"
            placeholder="https://…"
            value={draft.photoUrl}
            onChange={(e) => setDraft({ ...draft, photoUrl: e.currentTarget.value })}
          />
          <TextInput
            label="URL libretto / documenti"
            placeholder="https://…"
            value={draft.documentUrl}
            onChange={(e) => setDraft({ ...draft, documentUrl: e.currentTarget.value })}
          />
        </Group>

        <Switch
          label="Bloccato"
          description="Il mezzo non è prenotabile fino a sblocco"
          checked={draft.isBlocked}
          onChange={(e) => setDraft({ ...draft, isBlocked: e.currentTarget.checked })}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" onClick={() => { reset(); onClose(); }}>Annulla</Button>
          <Button
            leftSection={<IconCheck size={16} />}
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Aggiungi al parco
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SEED DEMO BUTTON — crea 5 veicoli demo via JSON:API
// ─────────────────────────────────────────────────────────────────────────────

interface DemoSpec {
  name: string;
  code: string;
  vehicleType: number;
  fuelType: string;
  currentKm: number;
  color: string;
  insuranceMonths: number;
  revisionMonths: number;
  taxMonths: number;
}

const DEMO_FLEET: DemoSpec[] = [
  { name: 'Fiat 500 EV', code: 'EV01', vehicleType: 1, fuelType: 'electric', currentKm: 12500, color: '#1E88E5', insuranceMonths: 8, revisionMonths: 15, taxMonths: 3 },
  { name: 'Tesla Model 3', code: 'EV02', vehicleType: 1, fuelType: 'electric', currentKm: 28400, color: '#FAFAFA', insuranceMonths: 11, revisionMonths: 24, taxMonths: 6 },
  { name: 'Ford Transit', code: 'VAN01', vehicleType: 1, fuelType: 'diesel', currentKm: 87600, color: '#616161', insuranceMonths: 2, revisionMonths: 10, taxMonths: 1 },
  { name: 'BMW Serie 3', code: 'AUTO01', vehicleType: 1, fuelType: 'hybrid', currentKm: 45200, color: '#212121', insuranceMonths: 6, revisionMonths: -1, taxMonths: 9 },
  { name: 'Sala Riunioni A', code: 'MR-A', vehicleType: 2, fuelType: '', currentKm: 0, color: '#7E57C2', insuranceMonths: 0, revisionMonths: 0, taxMonths: 0 },
];

function SeedDemoButton({ onSeeded }: { onSeeded: () => void }) {
  const seed = useMutation({
    mutationFn: async () => {
      const now = dayjs();
      let created = 0;
      for (const d of DEMO_FLEET) {
        const offset = (m: number) => m === 0 ? undefined : now.add(m, 'month').format('YYYY-MM-DD');
        try {
          await createOne('vehicles', {
            attributes: {
              name: d.name,
              code: d.code,
              vehicleType: d.vehicleType,
              icon: d.vehicleType,
              fuelType: d.fuelType || undefined,
              currentKm: d.currentKm || undefined,
              color: d.color,
              priority: 1,
              isBlocked: false,
              insuranceExpiry: offset(d.insuranceMonths),
              revisionExpiry: offset(d.revisionMonths),
              taxExpiry: offset(d.taxMonths),
            },
          });
          created++;
        } catch {
          // Continua: probabilmente codice già esistente.
        }
      }
      return created;
    },
    onSuccess: (count) => {
      notifications.show({
        color: count > 0 ? 'green' : 'orange',
        message: count > 0 ? `${count} mezzi demo creati` : 'Nessun mezzo creato (forse già presenti)',
      });
      onSeeded();
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore seed demo' }),
  });

  return (
    <Tooltip label="Crea 5 mezzi di esempio nel parco (4 auto + 1 sala)">
      <Button
        variant="light"
        color="grape"
        leftSection={<IconSparkles size={16} />}
        loading={seed.isPending}
        onClick={() => seed.mutate()}
      >
        Aggiungi demo
      </Button>
    </Tooltip>
  );
}
